import { ImportedModelResourceScope } from './ImportedModelResourceScope'

/**
 * 局部导入模型资源作用域的原子 revision。
 *
 * 该类在 prepare 阶段取得全部下一版句柄，但不改写 owner；`publish()` 只转交新句柄并
 * 发布外部 revision，`retirePreviousScopes()` 在 topology 退役成功后再释放旧句柄。
 * 发布失败时自动恢复旧 owner 并释放全部候选句柄。
 */
export class ImportedModelResourceRevision {
  /** revision 当前阶段，防止发布、退役与回滚发生越序。 */
  private state: 'prepared' | 'published' | 'retired' | 'rolled-back' = 'prepared'

  /** 发布成功后等待 topology 退役完成的旧作用域。 */
  private readonly pendingPreviousScopes = new Set<PuppyCAD.ImportedModelResourceScopeHandle>()

  /** 当前组合 owner；revision 结束后清空，避免事务对象延长业务结果生命周期。 */
  private currentOwner: PuppyCAD.ImportedModelResourceScopeOwner | null

  /** 候选 owner；revision 结束后清空。 */
  private replacementOwner: PuppyCAD.ImportedModelResourceScopeOwner | null

  /** 当前组合 owner 原始作用域。 */
  private previousCurrentScope: PuppyCAD.ImportedModelResourceScopeHandle | undefined

  /** 候选 owner 原始作用域。 */
  private previousReplacementScope: PuppyCAD.ImportedModelResourceScopeHandle | undefined

  /** 当前组合 owner 下一版聚合作用域。 */
  private nextCurrentScope: PuppyCAD.ImportedModelResourceScopeHandle | undefined

  /** 继续复用的 package owner 与新旧作用域映射。 */
  private retainedOwnerScopes: Array<{
    owner: PuppyCAD.ImportedModelResourceScopeOwner
    previousScope: PuppyCAD.ImportedModelResourceScopeHandle
    nextScope: PuppyCAD.ImportedModelResourceScopeHandle
  }>

  /**
   * 原子准备下一版资源 owner。
   *
   * @param input 当前、候选、复用 package owner 与替换根。
   * @returns 尚未改写任何 owner 的资源 revision。
   */
  public static prepare(
    input: PuppyCAD.ImportedModelResourceRevisionPrepareInput,
  ): ImportedModelResourceRevision {
    const preparedScopes: PuppyCAD.ImportedModelResourceScopeHandle[] = []
    try {
      const nextCurrentChildren: PuppyCAD.ImportedModelResourceScopeHandle[] = []
      if (input.currentOwner.resourceScope) {
        const retainedCurrent = input.currentOwner.resourceScope
          .retainExcludingRoots(input.replacedRoots)
        preparedScopes.push(retainedCurrent)
        nextCurrentChildren.push(retainedCurrent)
      }
      if (input.replacementOwner?.resourceScope) {
        const retainedReplacement = input.replacementOwner.resourceScope.retain()
        preparedScopes.push(retainedReplacement)
        nextCurrentChildren.push(retainedReplacement)
      }
      const nextCurrentScope = nextCurrentChildren.length > 0
        ? ImportedModelResourceScope.adoptAll(nextCurrentChildren)
        : undefined
      if (nextCurrentScope) {
        preparedScopes.splice(0, nextCurrentChildren.length, nextCurrentScope)
      }

      const retainedOwnerScopes = (input.retainedOwners ?? []).flatMap((owner) => {
        const previousScope = owner.resourceScope
        if (!previousScope) {
          return []
        }
        const nextScope = previousScope.retainExcludingRoots(input.replacedRoots)
        preparedScopes.push(nextScope)
        return [{ owner, previousScope, nextScope }]
      })

      return new ImportedModelResourceRevision(
        input.currentOwner,
        input.replacementOwner,
        input.currentOwner.resourceScope,
        input.replacementOwner?.resourceScope,
        nextCurrentScope,
        retainedOwnerScopes,
      )
    } catch (prepareError) {
      const rollbackErrors = this.releaseScopes(preparedScopes)
      if (rollbackErrors.length > 0) {
        throw new AggregateError(
          [prepareError, ...rollbackErrors],
          '导入模型资源 revision 准备失败且回滚不完整',
        )
      }
      throw prepareError
    }
  }

  /**
   * 创建已完整准备的 revision。
   *
   * @param currentOwner 当前组合结果 owner。
   * @param replacementOwner 候选子树 owner。
   * @param previousCurrentScope 当前组合 owner 原始作用域。
   * @param previousReplacementScope 候选 owner 原始作用域。
   * @param nextCurrentScope 下一版聚合作用域。
   * @param retainedOwnerScopes 复用 package owner 的新旧作用域。
   */
  private constructor(
    currentOwner: PuppyCAD.ImportedModelResourceScopeOwner,
    replacementOwner: PuppyCAD.ImportedModelResourceScopeOwner | undefined,
    previousCurrentScope: PuppyCAD.ImportedModelResourceScopeHandle | undefined,
    previousReplacementScope: PuppyCAD.ImportedModelResourceScopeHandle | undefined,
    nextCurrentScope: PuppyCAD.ImportedModelResourceScopeHandle | undefined,
    retainedOwnerScopes: Array<{
      owner: PuppyCAD.ImportedModelResourceScopeOwner
      previousScope: PuppyCAD.ImportedModelResourceScopeHandle
      nextScope: PuppyCAD.ImportedModelResourceScopeHandle
    }>,
  ) {
    this.currentOwner = currentOwner
    this.replacementOwner = replacementOwner ?? null
    this.previousCurrentScope = previousCurrentScope
    this.previousReplacementScope = previousReplacementScope
    this.nextCurrentScope = nextCurrentScope
    this.retainedOwnerScopes = retainedOwnerScopes
    this.collectPreviousScopes().forEach(scope => this.pendingPreviousScopes.add(scope))
  }

  /**
   * 原子转交新 owner并发布外部 revision，但暂不淘汰旧 owner。
   *
   * @param publish 已完成场景与 Worker 提交后的同步 WorkAssembly 发布回调。
   */
  public publish(publish: () => void): void {
    this.assertPrepared()
    this.publishNextOwners()
    try {
      publish()
    } catch (publishError) {
      this.restorePreviousOwners()
      const rollbackErrors = ImportedModelResourceRevision.releaseScopes(this.collectNextScopes())
      this.finishRollback()
      if (rollbackErrors.length > 0) {
        throw new AggregateError(
          [publishError, ...rollbackErrors],
          '导入模型资源 revision 发布失败且回滚不完整',
        )
      }
      throw publishError
    }

    this.state = 'published'
    // 发布成功后新 scope 已由业务结果持有；revision 只保留待退役旧 scope，不延长场景 owner 生命周期。
    this.clearOwnerReferences()
  }

  /**
   * topology 已完成退役后释放旧资源作用域。
   *
   * 单个 scope 失败时保留其句柄，后续调用只重试失败项；全部成功后进入 retired 状态。
   *
   * @returns 本次释放失败列表。
   */
  public retirePreviousScopes(): readonly unknown[] {
    if (this.state === 'retired') {
      return []
    }
    if (this.state !== 'published') {
      throw new Error('导入模型资源 revision 尚未发布，不能退役旧作用域')
    }
    const errors: unknown[] = []
    ;[...this.pendingPreviousScopes].reverse().forEach((scope) => {
      try {
        scope.release()
        this.pendingPreviousScopes.delete(scope)
      } catch (error) {
        errors.push(error)
      }
    })
    if (this.pendingPreviousScopes.size === 0) {
      this.state = 'retired'
    }
    return errors
  }

  /** 发布前失败时释放全部候选句柄；重复调用无副作用。 */
  public rollback(): void {
    if (this.state !== 'prepared') {
      return
    }
    const rollbackErrors = ImportedModelResourceRevision.releaseScopes(this.collectNextScopes())
    this.finishRollback()
    if (rollbackErrors.length > 0) {
      throw new AggregateError(rollbackErrors, '导入模型资源 revision 回滚失败')
    }
  }

  /** 把下一版作用域转交给真实 owner，但尚不释放旧 owner。 */
  private publishNextOwners(): void {
    const currentOwner = this.currentOwner
    if (!currentOwner) {
      throw new Error('导入模型资源 revision 缺少当前 owner')
    }
    if (this.nextCurrentScope) {
      currentOwner.resourceScope = this.nextCurrentScope
    } else {
      delete currentOwner.resourceScope
    }
    if (this.replacementOwner) {
      delete this.replacementOwner.resourceScope
    }
    this.retainedOwnerScopes.forEach(({ owner, nextScope }) => {
      owner.resourceScope = nextScope
    })
  }

  /** WorkAssembly 发布失败时恢复所有 owner 的原始作用域。 */
  private restorePreviousOwners(): void {
    const currentOwner = this.currentOwner
    if (!currentOwner) {
      return
    }
    this.assignScope(currentOwner, this.previousCurrentScope)
    if (this.replacementOwner) {
      this.assignScope(this.replacementOwner, this.previousReplacementScope)
    }
    this.retainedOwnerScopes.forEach(({ owner, previousScope }) => {
      owner.resourceScope = previousScope
    })
  }

  /** 收集当前 revision 独占的全部候选句柄。 */
  private collectNextScopes(): PuppyCAD.ImportedModelResourceScopeHandle[] {
    return [
      this.nextCurrentScope,
      ...this.retainedOwnerScopes.map(({ nextScope }) => nextScope),
    ].filter((scope): scope is PuppyCAD.ImportedModelResourceScopeHandle => Boolean(scope))
  }

  /** 收集发布成功后应淘汰的全部旧句柄。 */
  private collectPreviousScopes(): PuppyCAD.ImportedModelResourceScopeHandle[] {
    return [
      this.previousCurrentScope,
      this.previousReplacementScope,
      ...this.retainedOwnerScopes.map(({ previousScope }) => previousScope),
    ].filter((scope): scope is PuppyCAD.ImportedModelResourceScopeHandle => Boolean(scope))
  }

  /** 发布成功后断开业务 owner 与 next scope 引用，只保留待退役旧 scope。 */
  private clearOwnerReferences(): void {
    this.currentOwner = null
    this.replacementOwner = null
    this.previousCurrentScope = undefined
    this.previousReplacementScope = undefined
    this.nextCurrentScope = undefined
    this.retainedOwnerScopes = []
  }

  /** 回滚结束并清除 revision 持有的全部引用。 */
  private finishRollback(): void {
    this.state = 'rolled-back'
    this.clearOwnerReferences()
    this.pendingPreviousScopes.clear()
  }

  /** 为 owner 恢复可选 scope。 */
  private assignScope(
    owner: PuppyCAD.ImportedModelResourceScopeOwner,
    scope: PuppyCAD.ImportedModelResourceScopeHandle | undefined,
  ): void {
    if (scope) {
      owner.resourceScope = scope
    } else {
      delete owner.resourceScope
    }
  }

  /** 释放去重后的作用域，并确保单项异常不阻断后续清理。 */
  private static releaseScopes(
    scopes: readonly PuppyCAD.ImportedModelResourceScopeHandle[],
  ): unknown[] {
    const errors: unknown[] = []
    ;[...new Set(scopes)].reverse().forEach((scope) => {
      try {
        scope.release()
      } catch (error) {
        errors.push(error)
      }
    })
    return errors
  }

  /** 已提交或回滚的 revision 禁止再次提交。 */
  private assertPrepared(): void {
    if (this.state !== 'prepared') {
      throw new Error('导入模型资源 revision 已结束，不能再次提交')
    }
  }
}
